import React, {ChangeEvent, useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router";
import LocalBreadcrumbs from "../../components/Breadcrumbs/LocalBreadcrumbs";
import Api from "../../services/Api";
import {VisionStatementInterface, VisionStatementGoal, VisionStatementActivity} from "../DevelopmentPlan/interfaces";
import {renderOnTrack} from "../DevelopmentPlan/helpers";
import {Modal, ModalHeader, ModalBody, ModalFooter} from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import {useTranslation} from "react-i18next";
import {getLanguage} from "../../lang/helpers";


interface TeamMember {
  clientId: string;
  name: string;
}

interface TeamLog {
  id: string;
  logType: number;
  miscNumber: number;
  comment: string;
  created: string;
}

const TeamVisionStatement: React.FC = () => {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(); // i18n translation hook
  const lang = getLanguage(i18n.language);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [teamName, setTeamName] = useState<string>("");
  const [visionStatement, setVisionStatement] = useState<VisionStatementInterface | null>(null);
  const [goals, setGoals] = useState<Array<VisionStatementGoal>>([]);
  const [logs, setLogs] = useState<Array<TeamLog>>([]);
  const [editLog, setEditLog] = useState<TeamLog | null>(null);
  const [activities, setActivities] = useState<Array<VisionStatementActivity>>([]);
  const [member, setMember] = useState<TeamMember | null>(null);
  const { visionStatementId } = useParams();

  useEffect(() => {
    getData();
  }, []);

  const getData = () => {
    api
      .get("client/team/vision_statement/" + visionStatementId)
      .then((response) => {
        if (response.json.error) {
          setError(response.json.error);
        } else {
          setTeamName(response.json.teamName);
          setMember(response.json.member);
          setVisionStatement(response.json.visionStatement);
          setGoals(response.json.goals);
          setActivities(response.json.activities);
          setLogs(response.json.logs);
          setLoaded(true);
        }
      })
      .catch((error) => {
        alert("Unknown error (http status " + error.response.status + ")");
      });
  };

  if (error.length > 0) {
    return <div className="mt-4">Error: {error}</div>;
  }

  if (!loaded) {
    return <div className="mt-4">Loading...</div>;
  }

  if (!member || !visionStatement) {
    return <div className="mt-4">Missing data!?</div>;
  }

  const formatLogType = (logType: number) => {
    if (logType === 0) {
      return "Note";
    } else if (logType === 1) {
      return "Rating";
    } else if (logType === 2) {
      return "Progress";
    } else if (logType === 3) {
      return "Blocker";
    }
    return "Unknown";
  }

  const saveLog = () => {
    if (!editLog) {
      return;
    }

    api.post('client/team/vision_statement_log/' + visionStatementId, editLog).then(response => {
      setEditLog(null);
      getData();
    })

    console.log("Saving...");

    setEditLog(null);
  }

  const updateEditLog = (val: string, what: string) => {
    if (editLog) {
      const _editLog = {...editLog};
      if (what === "logType") {
        _editLog.logType = parseInt(val);
      } else if (what === "comment") {
        _editLog.comment = val;
      } else if (what === "miscNumber") {
        _editLog.miscNumber = parseInt(val);
      }
      setEditLog(_editLog);
    }
  }

  return (
    <>
      <LocalBreadcrumbs crumbs={[
        {name: "Team", url: "/team"},
        {name: member.name, url: "/team/member/" + member.clientId},
        {name: visionStatement.title}
      ]}/>

      <div className="mt-4 z-heading1">
        {visionStatement ? visionStatement.title : "N/A"}
      </div>

      <div className="flex flex-wrap gap-4 mt-6">
        <div className="main-half-col ">
          <p className="z-title2">
            Goal
          </p>
          <div className="bg-indigo-900 px-4 py-2 mt-2 rounded-md">

            <div className="my-2">
              <div className="flex items-center justify-between">
                <p className="z-title2">{visionStatement.title}</p>
                {renderOnTrack(visionStatement.onTrack, lang)}
              </div>

              <p className="z-body2">Due date: {visionStatement.deadline}</p>
              <p className="z-body2">Progress: {visionStatement.progress}%</p>
              <p className="z-body2 mt-2">{visionStatement.description}</p>
            </div>
          </div>

          <div className="mt-4 z-title1">Sub-goals</div>
          {
            goals.map(goal => {
              return (
                <div className="bg-indigo-900 px-4 py-2 mt-2 rounded-md" key={"goal_" + goal.id}>
                  <p className="z-title2">{goal.title}</p>
                  <p className="z-body2">Due date: {goal.deadline}</p>
                  <p className="z-body2">Done: {goal.status === 1 ? "Yes" : "No"}</p>
                  <p className="z-body2 mt-2">{goal.description}</p>
                  {
                    goal.actions.length > 0 &&
                    <>
                      <hr className="border-indigo-700 mt-4" />
                      <p className="mt-2 z-title2">Actions under this goal:</p>
                      {
                        goal.actions.map(action => {
                          return (
                            <div className="mt-2" key={"action_" + action.id}>
                              <p className="z-title2">{action.title}</p>
                              <p className="z-body2">Due date: {action.deadline}</p>
                              <p className="z-body2">Done: {action.status === 1 ? "Yes" : "No"}</p>
                              <p className="z-body2 mt-2">{action.description}</p>
                            </div>
                          )
                        })
                      }
                    </>
                  }

                </div>
              )
            })
          }

          <div className="mt-4 z-title1">Activities</div>
          {
            activities.map((activity, i) => {
              return (
                <div className="bg-indigo-900 px-4 py-2 mt-2 rounded-md" key={"activity_" + i}>
                  <p className="z-title2">{activity.title}</p>
                  <p className="z-body2">Due date: {activity.deadline}</p>
                  <p className="z-body2">Done: {activity.status === 1 ? "Yes" : "No"}</p>
                </div>
              )
            })
          }

        </div>

        <div className="main-half-col">
          <p className="z-title2">
            Logs
          </p>
          <div className="bg-indigo-900 px-4 pt-2 pb-4 mt-2 rounded-md">

            {
              logs.length === 0 &&
              <div className="z-body1">Nothing logged yet</div>
            }
            {
              logs.map((log) => {
                return (
                  <div key={"log_" + log.id} className="mt-2">
                    <div className="flex items-center justify-between">
                      <p className="z-title2">{formatLogType(log.logType)}</p>
                      <p className="z-body3">{log.created.substring(0, 10)}</p>
                    </div>

                    {
                      log.logType === 1 &&
                      <p className="z-body1">Rating: {log.miscNumber}</p>
                    }
                    {
                      log.logType === 2 &&
                        <p className="z-body1">Progress: {log.miscNumber}</p>
                    }
                    <p className="z-body2 mt-2 whitespace-pre-line">{log.comment}</p>
                    <hr className="border-indigo-700 my-4"/>
                  </div>
                )
              })
            }

            <CustomButton buttonType="outlineWhiteBase" className="mt-4"
                          onClick={() => setEditLog({
                            id: "0",
                            created: "",
                            comment: "",
                            logType: 0,
                            miscNumber: 0,
                          })}>
              Add Goal Log
            </CustomButton>

          </div>
        </div>
      </div>

      {
        editLog &&
        <Modal isOpen={true} onClose={() => setEditLog(null)}>
          <ModalHeader onClose={() => setEditLog(null)}>
            {
              editLog.id === "0" ? "Add log" : "Edit log"
            }
          </ModalHeader>
          <ModalBody>
              <div>
                  <select className="z-select" value={editLog.logType}
                          onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                            updateEditLog(e.target.value, "logType");
                          }}>
                      <option value={0}>Note</option>
                      <option value={1}>Rating</option>
                      <option value={2}>Progress</option>
                      <option value={3}>Blocker</option>
                  </select>
              </div>
              <div className="mt-2">
                  <p className="z-body3">Comments:</p>
                  <textarea value={editLog.comment} placeholder="Write your comments here..." className="z-input"
                            onChange={(e) => {
                    updateEditLog(e.target.value, "comment");
                  }}></textarea>
              </div>

              {
                editLog.logType === 1 &&
                  <div className="mt-2">
                      <p className="z-body3">Your rating:</p>
                      <input type="number" value={editLog.miscNumber} placeholder="Between 1 and 5" className="z-input"
                                onChange={(e) => {
                                  updateEditLog(e.target.value, "miscNumber");
                                }}></input>
                  </div>
              }

            {
              editLog.logType === 2 &&
                <div className="mt-2">
                    <p className="z-body3">Enter progress (0-100):</p>
                    <input type="number" value={editLog.miscNumber} placeholder="Between 1 and 5" className="z-input"
                           onChange={(e) => {
                             updateEditLog(e.target.value, "miscNumber");
                           }}></input>
                </div>
            }

            </ModalBody>
            <ModalFooter>
              <CustomButton onClick={() => saveLog()}>Save</CustomButton>
            </ModalFooter>
        </Modal>
      }
    </>
  );
};

export default TeamVisionStatement;
